import { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Label } from '@/components/ui/label';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const toAbsolute = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads')) return `${BACKEND}${url}`;
    return url;
};

export const ImageUpload = ({ value, onChange, label = 'Imagen', accept = 'image/jpeg,image/png,image/webp,image/gif', testId = 'image-upload' }) => {
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const preview = toAbsolute(value);

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Solo se permiten archivos de imagen');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('La imagen supera los 10 MB');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const absolute = toAbsolute(res.data.url);
            onChange(absolute);
            toast.success('Imagen subida');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'No se pudo subir la imagen';
            toast.error(msg);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div data-testid={testId}>
            {label ? <Label>{label}</Label> : null}
            <div className="mt-1 flex items-start gap-3">
                {preview ? (
                    <div className="relative shrink-0">
                        <img src={preview} alt="" className="size-24 rounded-xl object-cover border border-border" />
                        <button
                            type="button"
                            onClick={() => onChange('')}
                            data-testid={`${testId}-clear`}
                            className="absolute -top-2 -right-2 size-7 rounded-full bg-destructive text-destructive-foreground inline-flex items-center justify-center shadow"
                            aria-label="Eliminar imagen"
                        >
                            <X className="size-3.5" />
                        </button>
                    </div>
                ) : (
                    <div className="size-24 shrink-0 rounded-xl border-2 border-dashed border-border bg-secondary flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="size-7" />
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid={`${testId}-button`}
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60"
                    >
                        {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                        {uploading ? 'Subiendo…' : preview ? 'Reemplazar imagen' : 'Subir imagen'}
                    </button>
                    <p className="text-xs text-muted-foreground">JPG, PNG, WEBP o GIF · máx. 10 MB</p>
                    <input
                        ref={fileRef}
                        type="file"
                        accept={accept}
                        onChange={onPick}
                        className="hidden"
                        data-testid={`${testId}-input`}
                    />
                </div>
            </div>
        </div>
    );
};

export default ImageUpload;
